import axiosInstance from '../utils/axios.instance';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Service de gestion des utilisateurs
 */
export const userService = {
  /**
   * Récupérer le profil de l'utilisateur connecté
   * @returns {Promise}
   */
  getMyProfile: async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.USERS.ME);

      if (response.success && response.data) {
        // Mettre à jour le localStorage
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      }

      throw new Error('Réponse invalide');
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mettre à jour le profil
   * @param {Object} userData
   * @returns {Promise}
   */
  updateMyProfile: async (userData) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.UPDATE_ME, userData);

      if (response.success && response.data) {
        // Mettre à jour le localStorage
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      }

      throw new Error('Réponse invalide');
    } catch (error) {
      throw error;
    }
  },

  /**
   * Changer le mot de passe
   * @param {string} currentPassword
   * @param {string} newPassword
   * @param {string} confirmPassword
   * @returns {Promise}
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Liste de tous les utilisateurs (ADMIN)
   * @param {Object} params - Paramètres de pagination et filtres
   * @returns {Promise}
   */
  getUsers: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.USERS.LIST, { params });
      // L'intercepteur retourne { success: true, data: { content: [...], ... } }
      // On retourne le contenu du tableau
      return response.data?.content || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Détails d'un utilisateur (ADMIN)
   * @param {number} userId
   * @returns {Promise}
   */
  getUserById: async (userId) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.USERS.BY_ID(userId));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Modifier les rôles d'un utilisateur (ADMIN)
   * @param {number} userId
   * @param {Array} roles
   * @returns {Promise}
   */
  updateUserRole: async (userId, roles) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.UPDATE_ROLE(userId), {
        roleNames: roles,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Modifier le statut d'un utilisateur (ADMIN)
   * @param {number} userId
   * @param {boolean} enabled
   * @returns {Promise}
   */
  updateUserStatus: async (userId, enabled) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.UPDATE_STATUS(userId), {
        enabled,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Liste des utilisateurs en attente de validation (ADMIN)
   * @param {Object} params - Paramètres de pagination
   * @returns {Promise}
   */
  getPendingUsers: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/users/pending', { params });
      // L'intercepteur retourne { success: true, data: { content: [...], ... } }
      // On retourne le contenu du tableau
      return response.data?.content || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Activer un utilisateur en attente (ADMIN)
   * @param {number} userId
   * @returns {Promise}
   */
  activateUser: async (userId) => {
    try {
      const response = await axiosInstance.put(`/users/${userId}/activate`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Suspendre un utilisateur (ADMIN)
   * @param {number} userId
   * @returns {Promise}
   */
  suspendUser: async (userId) => {
    try {
      const response = await axiosInstance.put(`/users/${userId}/suspend`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Réactiver un utilisateur suspendu (ADMIN)
   * @param {number} userId
   * @returns {Promise}
   */
  reactivateUser: async (userId) => {
    try {
      const response = await axiosInstance.put(`/users/${userId}/reactivate`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les statistiques de l'utilisateur connecté
   * @returns {Promise} Statistiques incluant le stockage utilisé en octets
   */
  getMyStats: async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.USERS.STATS);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mettre à jour un utilisateur par son ID (ADMIN)
   * @param {number} userId
   * @param {Object} userData
   * @returns {Promise}
   */
  updateUserById: async (userId, userData) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.BY_ID(userId), userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Supprimer un utilisateur (ADMIN)
   * @param {number} userId
   * @returns {Promise}
   */
  deleteUser: async (userId) => {
    try {
      const response = await axiosInstance.delete(API_ENDPOINTS.USERS.BY_ID(userId));
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default userService;
